import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, GraduationCap, Users, Building2, KeyRound } from "lucide-react";

const EMPRESA = "ZAVIA";
const CONTACTO_EMAIL = "contacto@uaicardaccess.com";
const SITIO = "https://uaicardaccess.com";
const ACTUALIZADO = "Junio de 2026";

const COMUNES_SEGURIDAD = [
  "Ciframos las comunicaciones mediante HTTPS (TLS) y almacenamos la información en servidores con acceso restringido.",
  "El acceso a los datos está segmentado por rol (propietario, administrativo, docente, tutor y alumno); cada quien solo ve la información que le corresponde.",
  "No vendemos, rentamos ni comercializamos datos personales con terceros para fines publicitarios.",
];

const COMUNES_TERCEROS = [
  "Proveedor de infraestructura/hosting en la nube donde se aloja la plataforma.",
  "Inicio de sesión con Google (solo para cuentas del personal del colegio que lo utilizan).",
  "Videollamadas de clases en línea mediante un servicio de terceros (cuando el colegio activa el Aula Virtual con video).",
];

const DERECHOS = [
  "Acceder a los datos personales que tratamos.",
  "Rectificar datos inexactos o incompletos.",
  "Cancelar o solicitar la eliminación cuando ya no sean necesarios.",
  "Oponerte al tratamiento para finalidades específicas.",
];

const POLICIES = {
  general: {
    key: "general",
    icon: ShieldCheck,
    color: "text-navy",
    title: "Aviso de Privacidad — ZAVIA",
    subtitle: "Plataforma principal (Propietario, Administrativo y Usuario)",
    intro: `Este Aviso de Privacidad describe cómo ${EMPRESA} (en adelante, "la Plataforma") recaba, usa y protege la información de las instituciones educativas y de su personal (propietarios, personal administrativo y usuarios) que utilizan el sistema de control de acceso y asistencia escolar por NFC.`,
    datos: [
      "Datos de identificación del personal: nombre, correo electrónico, rol/cargo y fotografía de perfil.",
      "Datos de la institución: nombre del colegio, logotipo, domicilio y datos de contacto.",
      "Registros de asistencia y acceso generados por tarjetas NFC, código QR o verificación biométrica (huella/facial), incluyendo fecha y hora.",
      "Información administrativa y de cobranza: conceptos de pago, estados de cuenta y comprobantes cargados.",
      "Comunicaciones internas (chat, comunicados) generadas dentro de la plataforma.",
    ],
    finalidades: [
      "Operar el registro automático de entradas y salidas del plantel.",
      "Administrar expedientes de alumnos, personal y grupos.",
      "Gestionar cobranza, estados de cuenta y verificación de pagos.",
      "Permitir la comunicación interna entre las áreas del colegio.",
      "Generar reportes y estadísticas para la administración escolar.",
    ],
    biometrico: true,
    menores: true,
  },
  padres: {
    key: "padres",
    icon: Users,
    color: "text-navy",
    title: "Aviso de Privacidad — Portal de Padres",
    subtitle: "App / portal para tutores",
    intro: `Este Aviso de Privacidad describe cómo el Portal de Padres de ${EMPRESA} trata la información de los padres, madres o tutores que consultan la información escolar de sus hijos.`,
    datos: [
      "El UID (identificador) de la credencial del alumno, que el tutor ingresa para acceder.",
      "Información del alumno visible para el tutor: nombre, grado/grupo, asistencia y estado de cuenta.",
      "Datos de pago por transferencia: concepto, monto y comprobante que el tutor carga voluntariamente.",
      "Solicitudes de salida/entrega: medio de llegada, hora estimada, placas y color del vehículo (opcionales).",
      "Mensajes enviados al colegio o al docente y entregas de tareas del Aula Virtual.",
    ],
    finalidades: [
      "Mostrar al tutor la asistencia y puntualidad de su hijo/a en tiempo real.",
      "Consultar el estado de cuenta y registrar pagos por transferencia con comprobante.",
      "Coordinar la salida/entrega segura del alumno.",
      "Facilitar la comunicación con el colegio y el seguimiento de tareas.",
    ],
    biometrico: false,
    menores: true,
  },
  alumno: {
    key: "alumno",
    icon: GraduationCap,
    color: "text-navy",
    title: "Aviso de Privacidad — Portal del Alumno",
    subtitle: "App / portal para estudiantes",
    intro: `Este Aviso de Privacidad describe cómo el Portal del Alumno de ${EMPRESA} trata la información de los estudiantes que acceden a su aula virtual con el UID de su credencial. Al tratarse de menores de edad, el uso se realiza bajo la autorización y supervisión del colegio y de los padres o tutores.`,
    datos: [
      "El UID de la credencial del alumno, usado para iniciar sesión.",
      "Datos escolares del alumno: nombre, grado, grupo y nivel.",
      "Tareas y entregas: archivos, notas y calificaciones asociadas a las actividades.",
      "Contenido del cuaderno digital y de los recursos de biblioteca consultados.",
      "Mensajes intercambiados con sus maestros dentro de la plataforma.",
    ],
    finalidades: [
      "Mostrar al alumno sus tareas, avisos y calificaciones.",
      "Permitir la entrega de tareas y el uso del cuaderno digital.",
      "Dar acceso a libros y recursos educativos de su nivel.",
      "Facilitar la comunicación con sus maestros para dudas escolares.",
    ],
    biometrico: false,
    menores: true,
  },
  docentes: {
    key: "docentes",
    icon: Building2,
    color: "text-navy",
    title: "Aviso de Privacidad — Portal de Docentes",
    subtitle: "App / portal para maestros",
    intro: `Este Aviso de Privacidad describe cómo el Portal de Docentes de ${EMPRESA} trata la información de los maestros que utilizan la plataforma con su cuenta institucional.`,
    datos: [
      "Datos de identificación del docente: nombre, correo institucional, materia/cargo y fotografía de perfil.",
      "Grupos y alumnos asignados, así como los registros de asistencia que captura.",
      "Calificaciones, planeaciones, incidencias y control de clubs generados por el docente.",
      "Publicaciones del Aula Virtual (tareas, avisos y materiales) y su retroalimentación.",
      "Mensajes con alumnos, tutores y personal del colegio dentro de la plataforma.",
    ],
    finalidades: [
      "Registrar y consultar la asistencia de los grupos asignados.",
      "Capturar calificaciones, planeaciones e incidencias.",
      "Administrar el Aula Virtual y la comunicación con alumnos y tutores.",
      "Generar reportes y expedientes de asistencia (clubs, grupos).",
    ],
    biometrico: false,
    menores: false,
  },
};

const ORDER = ["general", "padres", "alumno", "docentes"];

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-display font-bold text-xl text-navy mb-2">{title}</h2>
      <div className="text-[15px] text-nardo-700 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5">
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
  );
}

function PolicyIndex() {
  return (
    <div className="min-h-screen bg-nardo-50">
      <div className="h-[3px] w-full bg-navy" />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12" data-testid="privacy-index">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-nardo-500 hover:text-navy mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Inicio
        </Link>
        <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-5 h-5 text-navy" /><span className="text-[10px] uppercase tracking-[0.3em] text-navy">Privacidad</span></div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-navy">Avisos de Privacidad</h1>
        <p className="text-nardo-600 mt-3">Cada portal de {EMPRESA} cuenta con su propio Aviso de Privacidad. Selecciona el que necesitas:</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-6">
          {ORDER.map((k) => {
            const p = POLICIES[k];
            const Icon = p.icon;
            return (
              <Link key={k} to={`/privacidad-${k}`} data-testid={`privacy-link-${k}`}
                className="group flex items-center gap-3 bg-white border border-nardo-200 rounded-xl p-4 hover:border-navy hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-lg bg-navy/10 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-navy" /></div>
                <div className="min-w-0">
                  <div className="font-semibold text-navy truncate">{p.title.replace("Aviso de Privacidad — ", "")}</div>
                  <div className="text-xs text-nardo-500 truncate">{p.subtitle}</div>
                </div>
              </Link>
            );
          })}
        </div>
        <p className="text-xs text-nardo-400 mt-8">Última actualización: {ACTUALIZADO}</p>
      </div>
    </div>
  );
}

export default function PrivacyPolicy({ portal: portalProp }) {
  const params = useParams();
  const portal = portalProp || params.portal;
  if (!portal) return <PolicyIndex />;
  const p = POLICIES[portal] || POLICIES.general;
  const Icon = p.icon;

  return (
    <div className="min-h-screen bg-nardo-50">
      <div className="h-[3px] w-full bg-navy" />
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-12" data-testid={`privacy-${p.key}`}>
        <Link to="/privacidad" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-nardo-500 hover:text-navy mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Todos los avisos
        </Link>
        <div className="flex items-center gap-2 mb-2"><Icon className={`w-5 h-5 ${p.color}`} /><span className="text-[10px] uppercase tracking-[0.3em] text-navy">{p.subtitle}</span></div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-navy leading-tight">{p.title}</h1>
        <p className="text-xs text-nardo-400 mt-2">Última actualización: {ACTUALIZADO}</p>

        <p className="text-[15px] text-nardo-700 leading-relaxed mt-6">{p.intro}</p>

        <Section title="1. Responsable del tratamiento">
          <p>{EMPRESA} es la empresa desarrolladora que provee la plataforma tecnológica. Cada institución educativa que la utiliza es corresponsable del tratamiento de los datos de su comunidad escolar. Para cualquier asunto de privacidad puedes escribir a <a href={`mailto:${CONTACTO_EMAIL}`} className="text-navy underline">{CONTACTO_EMAIL}</a> o visitar <a href={SITIO} className="text-navy underline">{SITIO}</a>.</p>
        </Section>

        <Section title="2. Datos personales que tratamos">
          <Bullets items={p.datos} />
        </Section>

        <Section title="3. Finalidades del tratamiento">
          <Bullets items={p.finalidades} />
        </Section>

        {p.biometrico && (
          <Section title="4. Datos biométricos (huella / reconocimiento facial)">
            <p>Cuando el colegio activa la verificación biométrica, los datos se utilizan <strong>únicamente</strong> para confirmar la identidad al registrar la asistencia. Se procesan de forma segura, no se comparten con terceros y se conservan solo mientras el alumno o el personal esté activo en la institución. Su captura requiere el consentimiento del titular o, en el caso de menores, de su padre, madre o tutor.</p>
          </Section>
        )}

        <Section title={p.biometrico ? "5. Menores de edad" : "4. Menores de edad"}>
          {p.menores ? (
            <p>La plataforma trata datos de menores de edad exclusivamente con fines educativos y de seguridad escolar, bajo la responsabilidad del colegio y con la autorización de los padres o tutores. No se solicita a los menores información distinta a la necesaria para su actividad escolar, ni se utiliza con fines publicitarios.</p>
          ) : (
            <p>Este portal está dirigido a personal adulto (docentes) de la institución. En el ejercicio de sus funciones, el docente puede tratar datos de alumnos menores de edad, siempre con fines educativos y bajo las políticas del colegio.</p>
          )}
        </Section>

        <Section title={p.biometrico ? "6. Con quién se comparte" : "5. Con quién se comparte"}>
          <p>Solo compartimos información con los proveedores estrictamente necesarios para operar el servicio:</p>
          <Bullets items={COMUNES_TERCEROS} />
          <p className="mt-2">No compartimos datos con terceros para publicidad ni los vendemos.</p>
        </Section>

        <Section title={p.biometrico ? "7. Seguridad y conservación" : "6. Seguridad y conservación"}>
          <Bullets items={COMUNES_SEGURIDAD} />
          <p className="mt-2">Conservamos los datos durante el tiempo que el titular permanezca vinculado a la institución y por los plazos que exija la normativa aplicable. Después se eliminan o anonimizan.</p>
        </Section>

        <Section title={p.biometrico ? "8. Tus derechos" : "7. Tus derechos"}>
          <p>En cualquier momento puedes ejercer tus derechos para:</p>
          <Bullets items={DERECHOS} />
          <p className="mt-2">Para ejercerlos, contacta directamente al colegio o escríbenos a <a href={`mailto:${CONTACTO_EMAIL}`} className="text-navy underline">{CONTACTO_EMAIL}</a>.</p>
        </Section>

        <Section title={p.biometrico ? "9. Cambios a este aviso" : "8. Cambios a este aviso"}>
          <p>Podemos actualizar este Aviso de Privacidad para reflejar cambios en el servicio o en la normativa. Publicaremos la versión vigente en esta misma dirección, indicando la fecha de última actualización.</p>
        </Section>

        <Section title={p.biometrico ? "10. Contacto" : "9. Contacto"}>
          <p>Si tienes dudas sobre este Aviso de Privacidad, escríbenos a <a href={`mailto:${CONTACTO_EMAIL}`} className="text-navy underline">{CONTACTO_EMAIL}</a>.</p>
        </Section>

        <div className="mt-12 pt-6 border-t border-nardo-200 text-xs text-nardo-400 flex items-center gap-1.5">
          <KeyRound className="w-3.5 h-3.5" /> © {new Date().getFullYear()} {EMPRESA} — Todos los derechos reservados.
        </div>
      </article>
    </div>
  );
}
